/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import {useIntl} from 'react-intl'
import {validatePassword} from '@salesforce/retail-react-app/app/utils/password-utils'

export default function useResetPasswordLandingFields({
    form: {
        control,
        formState: {errors},
        watch
    },
    prefix = ''
}) {
    const {formatMessage} = useIntl()

    const fields = {
        email: {
            name: `${prefix}email`,
            label: formatMessage({
                defaultMessage: 'Enter Your Email',
                id: 'use_login_fields.label.enter_your_email'
            }),
            placeholder: 'example@email.com',
            defaultValue: '',
            type: 'email',
            autoComplete: 'email',
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please enter a valid email address.',
                    id: 'use_reset_password_fields.error.required_email'
                })
            },
            error: errors[`${prefix}email`],
            control
        },
        password: {
            name: `${prefix}password`,
            label: formatMessage({
                defaultMessage: 'New Password',
                id: 'reset_password_landing_fields.label.new_password'
            }),
            type: 'password',
            autoComplete: 'new-password',
            defaultValue: '',
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please create a password.',
                    id: 'reset_password_landing_fields.error.required_password'
                }),
                validate: {
                    hasMinChars: (val) =>
                        validatePassword(val).hasMinChars ||
                        formatMessage({
                            defaultMessage: 'Password must contain at least 8 characters.',
                            id: 'reset_password_landing_fields.error.minimum_characters'
                        }),
                    hasUppercase: (val) =>
                        validatePassword(val).hasUppercase ||
                        formatMessage({
                            defaultMessage: 'Password must contain at least one uppercase letter.',
                            id: 'reset_password_landing_fields.error.uppercase_letter'
                        }),
                    hasLowercase: (val) =>
                        validatePassword(val).hasLowercase ||
                        formatMessage({
                            defaultMessage: 'Password must contain at least one lowercase letter.',
                            id: 'reset_password_landing_fields.error.lowercase_letter'
                        }),
                    hasNumber: (val) =>
                        validatePassword(val).hasNumber ||
                        formatMessage({
                            defaultMessage: 'Password must contain at least one number.',
                            id: 'reset_password_landing_fields.error.contain_number'
                        }),
                    hasSpecialChar: (val) =>
                        validatePassword(val).hasSpecialChar ||
                        formatMessage({
                            defaultMessage: 'Password must contain at least one special character.',
                            id: 'reset_password_landing_fields.error.special_character'
                        })
                }
            },
            error: errors[`${prefix}password`],
            control
        },
        confirmPassword: {
            name: `${prefix}confirmPassword`,
            label: formatMessage({
                defaultMessage: 'Confirm Password',
                id: 'reset_password_landing_fields.label.confirm_password'
            }),
            type: 'password',
            defaultValue: '',
            rules: {
                required: formatMessage({
                    defaultMessage: 'Please confirm password.',
                    id: 'reset_password_landing_fields.error.required_confirm_password'
                }),
                validate: (value) =>
                    value === watch(`${prefix}password`, '') ||
                    formatMessage({
                        defaultMessage: 'Passwords do not match',
                        id: 'reset_password_landing_fields.error.passwords_do_not_match'
                    })
            },
            error: errors[`${prefix}confirmPassword`],
            control
        }
    }

    return fields
}
